'use client';

import { useState } from 'react';
import { faFilter } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { AgeGroupFilterSection } from './age-group-filter-section.component';
import { BookingPreferencesFilterSection } from './booking-preferences-filter-section.component';
import { DateFilterSection } from './date-filter-section.component';
import { DayTimeFilterSection } from './day-time-filter-section.component';
import { DistanceFilterSection } from './distance-filter-section.component';
import { ProfessionalGenderFilterSection } from './professional-gender-filter-section.component';
import { SearchFiltersFooter } from './search-filters-footer.component';

import { Icon } from '@/components/ui/icon.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { Pill } from '@/components/ui/pill/pill.component';
import { Sheet, SheetClose, SheetContent } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useFiltersDraft } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-filters-draft.hook';
import {
  EmptyPreviewResult,
  useFiltersPreview,
} from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-filters-preview.hook';
import { useSearchFilter } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-search-filters.hook';
import { SearchModalityAnalyticsMap } from '@/app/(main)/(pages)/busca/[slug]/_types/search-sort.type';
import { getActiveFilters } from '@/app/(main)/(pages)/busca/[slug]/_utils/active-filters.util';
import { getFilterAnalyticsProperties } from '@/app/(main)/(pages)/busca/[slug]/_utils/filter-analytics.util';
import {
  isAgeGroupFilterAvailable,
  isBookingPreferencesFilterAvailable,
  isDateFilterAvailable,
  isDistanceFilterAvailable,
  isProfessionalGenderFilterAvailable,
} from '@/app/(main)/(pages)/busca/[slug]/_utils/filter-availability.util';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { SearchType } from '@/types/most-search-type';
import useDeviceType from '@/hooks/use-device-type.hook';
import { useUserLocation } from '@/hooks/use-user-location.hook';

interface SearchFiltersProps {
  slugs: string[];
  modality: SearchType;
  className?: string;
}

export function SearchFilters({ slugs, modality, className }: SearchFiltersProps) {
  const [open, setOpen] = useState(false);
  const { filters } = useSearchFilter();
  const analytics = getAnalyticsPlugin();
  const deviceType = useDeviceType();

  const activeFilters = getActiveFilters(filters, modality);
  const activeFiltersCount = activeFilters.length;
  const isActive = activeFiltersCount > 0;

  const handleOpen = (): void => {
    analytics.track(AnalyticsEnum.FILTERS_OPEN, {
      filtros_ativos: activeFilters,
      modalidade: SearchModalityAnalyticsMap[modality],
      device_type: deviceType,
    });
    setOpen(true);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Pill
        hasAction
        variant={isActive ? 'success' : 'light'}
        icon={faFilter}
        label={isActive ? `Filtros: ${activeFiltersCount}` : 'Filtros'}
        action={handleOpen}
        className={className}
        data-testid={DataTestIdEnum.SEARCH_FILTERS_TRIGGER}
      />

      <SheetContent side="centerFit" className="p-0 sm:max-w-[22.5rem]" hasHeader title="Filtros">
        <SearchFiltersContent slugs={slugs} modality={modality} onClose={() => setOpen(false)} />
      </SheetContent>
    </Sheet>
  );
}

interface SearchFiltersContentProps {
  slugs: string[];
  modality: SearchType;
  onClose: () => void;
}

function SearchFiltersContent({ slugs, modality, onClose }: SearchFiltersContentProps) {
  const { userLocation } = useUserLocation();
  const { filters } = useSearchFilter();
  const { draft, setDraft, reset, apply } = useFiltersDraft();
  const [isDateFilterValid, setIsDateFilterValid] = useState(true);
  const analytics = getAnalyticsPlugin();
  const deviceType = useDeviceType();

  const hasDistance = isDistanceFilterAvailable(modality);
  const hasAgeGroup = isAgeGroupFilterAvailable(modality);
  const hasProfessionalGender = isProfessionalGenderFilterAvailable(modality);
  const hasBookingPreferences = isBookingPreferencesFilterAvailable(modality);
  const hasDate = isDateFilterAvailable(modality);
  const hasValidOrigin = Boolean(userLocation?.latitude && userLocation?.longitude);
  const handleEmptyPreview = ({ filters: previewedFilters, procedureCodes }: EmptyPreviewResult): void => {
    analytics.track(AnalyticsEnum.FILTERS_NO_RESULTS_FOR_COMBINATION, {
      ...getFilterAnalyticsProperties(previewedFilters, modality),
      procedure_codes: procedureCodes,
      city: userLocation?.city,
      modalidade: SearchModalityAnalyticsMap[modality],
      device_type: deviceType,
    });
  };

  const preview = useFiltersPreview({
    slugs,
    draft,
    applied: filters,
    modality,
    enabled: !hasDistance || hasValidOrigin,
    onEmptyResult: handleEmptyPreview,
  });

  const handleApply = (): void => {
    if (hasDate && !isDateFilterValid) {
      return;
    }

    apply();
    analytics.track(AnalyticsEnum.FILTERS_APPLIED, {
      ...getFilterAnalyticsProperties(draft, modality),
      modalidade: SearchModalityAnalyticsMap[modality],
      device_type: deviceType,
    });
    onClose();
  };

  const handleClear = (): void => {
    const clearedFilters = getActiveFilters(draft, modality);

    reset();
    analytics.track(AnalyticsEnum.FILTERS_CLEARED, {
      filtros_ativos: clearedFilters,
      modalidade: SearchModalityAnalyticsMap[modality],
      device_type: deviceType,
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid={DataTestIdEnum.SEARCH_FILTERS_MODAL}>
      <div className="flex w-full items-center gap-2 px-5 pb-4 pt-5">
        <div className="flex flex-1 items-center gap-2">
          <Icon icon={faFilter} className="text-2xl text-green-500" aria-hidden />
          <Typography as="span" variant={TypographyVariant.MODAL_TITLE}>
            Filtros
          </Typography>
        </div>
        <SheetClose asChild>
          <ModalCloseButton />
        </SheetClose>
      </div>

      <div className="flex flex-1 flex-col gap-8 overflow-y-auto overflow-x-hidden px-6 pb-5">
        {hasAgeGroup && (
          <AgeGroupFilterSection value={draft.ageGroup} onChange={(ageGroup) => setDraft({ ageGroup })} />
        )}
        <DayTimeFilterSection value={draft.dayTime} onChange={(dayTime) => setDraft({ dayTime })} />
        {hasProfessionalGender && (
          <ProfessionalGenderFilterSection
            value={draft.professionalGender}
            onChange={(professionalGender) => setDraft({ professionalGender })}
          />
        )}
        {hasDistance && (
          <DistanceFilterSection
            value={draft.distance}
            onChange={(distance) => setDraft({ distance })}
            disabled={!hasValidOrigin}
          />
        )}
        {hasDate && (
          <DateFilterSection values={draft} onChange={setDraft} onValidityChange={setIsDateFilterValid} />
        )}
        {hasBookingPreferences && <BookingPreferencesFilterSection values={draft} onChange={setDraft} />}
      </div>

      <SearchFiltersFooter
        hasResults={preview.hasResults}
        isLoading={preview.isLoading}
        isError={preview.isError}
        isDraftValid={!hasDate || isDateFilterValid}
        onRetry={preview.retry}
        onClear={handleClear}
        onApply={handleApply}
      />
    </div>
  );
}
